import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { motion } from "framer-motion"

import * as React from "react"

import { SFIcon } from '@/components/ui/sf-icon'
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@packages/config/theme"
import { TOAST_VARIANTS } from "@supabank/ui/motion"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      // ✅ Using semantic tokens from SSOT (mobile-first, always visible)
      semanticTokens.toast.viewport.position,  // fixed top-0 left-0 right-0 (mobile) → bottom-right (desktop)
      semanticTokens.toast.viewport.zIndex,    // z-[100] (SSOT, above modals/menus)
      semanticTokens.toast.viewport.layout,    // flex max-h-screen w-full flex-col-reverse sm:flex-col
      semanticTokens.toast.viewport.padding,   // p-4
      semanticTokens.toast.viewport.maxWidth,  // w-full sm:max-w-[400px] (canonical)
      semanticTokens.toast.viewport.pointerEvents, // pointer-events-auto (always interactive)
      className
    )}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  // ✅ Using semantic tokens from SSOT (Liquid Glass Canon)
  cn(
    semanticTokens.toast.item.base,        // Base layout with canonical sizes and spacing
    semanticTokens.toast.item.glass,       // Liquid Glass effect (bg-white/50, backdrop-blur-2xl)
    semanticTokens.toast.item.width,       // w-full max-w-[400px] min-w-[320px]
    semanticTokens.toast.item.swipeTransition,  // transition-transform data-[swipe=...]
  ),
  {
    variants: {
      variant: {
        // ✅ Default variant: Liquid Glass with subtle border
        default: `group border border-border/60 ${semanticTokens.fg.default}`,
        // ✅ Success variant: green tones with Liquid Glass + accent border
        success: `group border-emerald-500/40 bg-emerald-500/5 ${semanticTokens.positive.fg}`,
        // ✅ Info variant: blue tones with Liquid Glass + accent border
        info: `group border-blue-500/40 bg-blue-500/5 ${semanticTokens.info.fg}`,
        // ✅ Warning variant: amber tones with Liquid Glass + accent border
        warning: `group border-amber-500/40 bg-amber-500/5 ${semanticTokens.warning.fg}`,
        // ✅ Error variant: red tones with Liquid Glass + accent border
        error: `group border-red-500/40 bg-red-500/5 ${semanticTokens.danger.fg}`,
        // ✅ Destructive variant: same as error but with destructive class for group styling
        destructive: `destructive group border-red-500/40 bg-red-500/5 ${semanticTokens.danger.fg}`,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const MotionToastRoot = React.forwardRef<
  React.ElementRef<typeof motion.div>,
  React.ComponentPropsWithoutRef<typeof motion.div>
>(({ className, ...props }, ref) => {
  const state = props["data-state"]
  const status = state === "open" ? "animate" : "exit"

  return (
    <motion.div
      ref={ref}
      className={className}
      variants={TOAST_VARIANTS}
      initial="initial"
      animate={status}
      {...props}
    />
  )
})
MotionToastRoot.displayName = "MotionToastRoot"

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
  VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root {...props} asChild>
      <MotionToastRoot ref={ref} className={cn(toastVariants({ variant }), className)}>
        {children}
      </MotionToastRoot>
    </ToastPrimitives.Root>
  )
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      // ✅ Using semantic tokens from SSOT
      semanticTokens.toast.action.base,        // inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium
      semanticTokens.toast.action.interactive, // ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
      semanticTokens.toast.action.disabled,   // disabled:pointer-events-none disabled:opacity-50
      semanticTokens.toast.action.destructive, // group-[.destructive]:... styles
      semanticTokens.motion.fast,              // transition-colors with 120ms duration
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      // ✅ Using semantic tokens from SSOT (canonical from SupaToast)
      semanticTokens.toast.close.position,    // absolute top-1/2 right-3 -translate-y-1/2 (centered vertically)
      semanticTokens.toast.close.size,        // p-1.5
      semanticTokens.toast.close.radius,      // rounded-full
      semanticTokens.toast.close.bg,          // bg-black/5 hover:bg-black/10 (glass-like)
      semanticTokens.toast.close.opacity,     // opacity-100 sm:opacity-0 (always visible on mobile)
      semanticTokens.toast.close.focus,       // focus:outline-none focus:ring-2
      semanticTokens.fg.muted,                // text-muted-foreground (auto dark mode)
      "hover:text-foreground",                // hover:text-foreground (auto dark mode)
      // Destructive variant styles (uses semantic tokens for dark mode)
      "group-[.destructive]:text-destructive-foreground group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive group-[.destructive]:focus:ring-offset-destructive",
      semanticTokens.motion.fast,              // transition-opacity with 120ms duration
      className
    )}
    toast-close=""
    {...props}
  >
    <SFIcon icon={sfIcons.close} className="h-3.5 w-3.5" />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(semanticTokens.typography.label, semanticTokens.fg.default, className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn(semanticTokens.typography.body, semanticTokens.fg.muted, className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, type ToastActionElement, type ToastProps
}

